/**
 * Custom: Free samples
 * -----------------------------------------------------------------------------
 * Replaces the single sample the Canvas build offers, in one of two modes.
 *
 * - All samples: gives every listed free sample at once, as read-only cards.
 *   The list is the `free_samples` metaobject (entry `free-samples`), read by
 *   the theme into `cart.freeSamples.list`. No list, no samples.
 * - Tiers: when `window.cnvs.freeSampleTiers` has entries (the
 *   `free_sample_tiers` metaobjects, printed by `theme-variables`), the list is
 *   the available products of the tier the cart total has reached. The
 *   customer holds `MAX_SAMPLES` of them, filled automatically in collection
 *   order and swapped by clicking a card.
 *
 * The compiled `freeSamplesHandler` in `stores/cart.js` keeps running either
 * way: it adds `freeProductsIds[0]` when the cart has products but no sample
 * from the current list, and removes one listed `_gift` line per cart update
 * once the last non-gift item has gone.
 *
 * Pairs with `custom.free-samples.css`, which turns the built-in radios into a
 * display and follows the `data-` attributes set by `paint()`.
 */
(function() {
  'use strict'

  /**
   * Cart status labels that mean no request is in flight. `setResponse` is
   * skipped while `itemsLeft` is non-zero, so the label is the only reliable
   * signal that `cart.response` is current.
   */
  var SETTLED_LABELS = ['loaded', 'updated']

  /**
   * Cart mutations cascade (the built-in handler reacts to our writes and we
   * react to its writes), so work is debounced until the cart falls quiet.
   */
  var DEBOUNCE = 250
  var BOOT_INTERVAL = 100
  var BOOT_ATTEMPTS = 100
  var GIFT_PROPERTY = '_gift'

  /**
   * Samples a cart may hold in tier mode.
   */
  var MAX_SAMPLES = 2

  var store = null
  var timer = null
  var paintTimer = null
  var running = false

  /**
   * Tiers sorted by threshold, lowest first. Empty outside tier mode.
   */
  var tiers = []

  /**
   * Heading from the `free_samples` entry, shown for tiers without a title.
   * - Captured from the compiled `SET_FREE_SAMPLES` commits, since ours
   *   overwrite `cart.freeSamples.title`.
   */
  var baseTitle = ''
  var committing = false

  /**
   * Variant IDs that failed to add, e.g. sold out between the Storefront API
   * read and the cart write. Dropped for the rest of the page view rather than
   * retried on every cart change.
   */
  var abandoned = {}

  /**
   * Find the Vuex store on the mounted Vue app.
   * @returns {Object|null}
   */
  function findStore() {
    var element = document.getElementById('vue-element')

    if (!element || !element.__vue_app__) {
      return null
    }

    var app = element.__vue_app__

    if (!app.config || !app.config.globalProperties) {
      return null
    }

    return app.config.globalProperties.$store || null
  }

  /**
   * Tiers printed by the theme, sorted by threshold.
   * - Entry order in the admin is not meaningful, so it is not relied on.
   * @returns {Array}
   */
  function readTiers() {
    var source = window.cnvs && window.cnvs.freeSampleTiers

    if (!Array.isArray(source)) {
      return []
    }

    return source
      .map(function(tier) {
        return {
          title: tier.title || '',
          threshold: Number(tier.threshold) || 0,
          products: tier.products || [],
        }
      })
      .sort(function(first, second) {
        return first.threshold - second.threshold
      })
  }

  /**
   * Is the tier mode on for this store?
   * @returns {Boolean}
   */
  function isTierMode() {
    return tiers.length > 0
  }

  /**
   * Current cart line items.
   * @returns {Array}
   */
  function getItems() {
    if (!store.state.cart || !store.state.cart.response) {
      return []
    }

    return store.state.cart.response.items || []
  }

  /**
   * Formatted free sample products.
   * @returns {Array}
   */
  function getSamples() {
    if (!store.state.cart || !store.state.cart.freeSamples) {
      return []
    }

    return store.state.cart.freeSamples.list || []
  }

  /**
   * First sellable variant of a sample product.
   * - `formatFreeSamples` and `theme-variables` already drop products that are
   *   not available for sale, so this is normally the only variant.
   * @param {Object} product - Formatted product.
   * @returns {Object|false}
   */
  function getVariant(product) {
    if (!product || !product.variants) {
      return false
    }

    for (var index = 0; index < product.variants.length; index += 1) {
      if (product.variants[index].available) {
        return product.variants[index]
      }
    }

    return false
  }

  /**
   * Sellable variant IDs of a sample list, in list order.
   * @param {Array} products - Formatted products.
   * @returns {Array}
   */
  function getVariantIds(products) {
    var ids = []

    products.forEach(function(product) {
      var variant = getVariant(product)

      if (variant) {
        ids.push(variant.id)
      }
    })

    return ids
  }

  /**
   * Is the line item one of our samples?
   * @param {Object} item - Cart line item.
   * @returns {Boolean}
   */
  function isGift(item) {
    return Boolean(item.properties && item.properties[GIFT_PROPERTY])
  }

  /**
   * Is the cart idle with a current response?
   * @returns {Boolean}
   */
  function isSettled() {
    if (!store.state.cart || !store.state.cart.status) {
      return false
    }

    return SETTLED_LABELS.indexOf(store.state.cart.status.label) >= 0
  }

  /**
   * Tier reached by the cart total, or `null` below the lowest threshold.
   * - `total_price` is in cents after discounts, the basis the free shipping
   *   bar uses. Samples cost 0, so they never move it.
   * @returns {Object|null}
   */
  function getActiveTier() {
    var response = store.state.cart && store.state.cart.response
    var total = response && response.total_price ? response.total_price : 0
    var active = null

    tiers.forEach(function(tier) {
      if (tier.threshold <= total) {
        active = tier
      }
    })

    return active
  }

  /**
   * Put the active tier's products into `cart.freeSamples`, so the compiled
   * component renders its cards and the compiled handler works from it.
   * - Only commits on a difference: the commit fires our own subscriber, and
   *   the compiled cart fetch commits the `free_samples` list once after the
   *   first cart load, which this then undoes on the next pass.
   */
  function syncTierList() {
    var tier = getActiveTier()
    var list = tier ? tier.products : []
    var title = tier && tier.title ? tier.title : baseTitle
    var current = store.state.cart.freeSamples || {}

    var sameTitle = current.title === title
    var sameList = getVariantIds(current.list || []).join(',') === getVariantIds(list).join(',')

    if (sameTitle && sameList) {
      return
    }

    committing = true

    try {
      store.commit('cart/SET_FREE_SAMPLES', {
        title: title,
        list: list.slice(),
      })
    } finally {
      committing = false
    }
  }

  /**
   * Add sample variants to the cart in a single request.
   * @param {Array} ids - Variant IDs.
   * @returns {Promise}
   */
  function addSamples(ids) {
    var items = ids.map(function(id) {
      var properties = {}
      properties[GIFT_PROPERTY] = true

      return {
        id: id,
        properties: properties,
        quantity: 1,
      }
    })

    return store.dispatch('cart/addItem', items)
  }

  /**
   * Add the missing samples, isolating any that cannot be added.
   * - A single `/cart/add.js` call fails as a whole, so on failure each sample
   *   is retried on its own and the offender is abandoned.
   * @param {Array} ids - Variant IDs.
   * @returns {Promise}
   */
  function addMissing(ids) {
    return addSamples(ids).catch(function() {
      return ids.reduce(function(chain, id) {
        return chain.then(function() {
          return addSamples([id]).catch(function() {
            abandoned[id] = true
          })
        })
      }, Promise.resolve())
    })
  }

  /**
   * Remove sample lines one at a time, by line key.
   * - Same call the compiled handler uses. A line it already removed makes
   *   the request fail, which is ignored.
   * @param {Array} lineItems - Cart line items to remove.
   * @returns {Promise}
   */
  function removeLines(lineItems) {
    return lineItems.reduce(function(chain, item) {
      return chain.then(function() {
        return store.dispatch('cart/updateItem', item.key).catch(function() {})
      })
    }, Promise.resolve())
  }

  /**
   * Reset samples that raced to a quantity above one.
   * - `/cart/add.js` increments an existing line rather than creating a second
   *   one, so a sample added twice shows as quantity two.
   * @param {Array} lineItems - Cart line items to reset.
   * @returns {Promise}
   */
  function collapseQuantities(lineItems) {
    var updates = lineItems.map(function(item) {
      return {
        id: item.key,
        quantity: 1,
      }
    })

    return store.dispatch('cart/updateItem', updates).catch(function() {})
  }

  /**
   * Mark up the rendered sample cards.
   * - Vue owns `class` on these elements but not `data-` attributes, so the
   *   state is written as attributes and styled by `custom.free-samples.css`.
   * - The built-in component always marks one card active and renders whether
   *   or not the cart holds a sample; this replaces that with the truth.
   */
  function paint() {
    if (!store) {
      return
    }

    var samples = getSamples()
    var items = getItems()
    var inCart = {}
    var hasProduct = false
    var tierMode = isTierMode()

    items.forEach(function(item) {
      if (isGift(item)) {
        inCart[item.id] = true
        return
      }

      hasProduct = true
    })

    var visible = hasProduct && samples.length ? 'true' : 'false'
    var roots = document.querySelectorAll('.free-samples')

    Array.prototype.forEach.call(roots, function(root) {
      root.setAttribute('data-free-samples-visible', visible)
      root.setAttribute('data-free-samples-mode', tierMode ? 'tiers' : 'all')

      var cards = root.querySelectorAll('.free-samples__card')

      Array.prototype.forEach.call(cards, function(card, index) {
        var variant = getVariant(samples[index])
        var present = Boolean(variant && inCart[variant.id])

        card.setAttribute('data-free-sample-in-cart', present ? 'true' : 'false')

        /**
         * In tier mode the card itself is the control, see `swap()`.
         */
        if (tierMode) {
          card.setAttribute('role', 'button')
          card.setAttribute('tabindex', present ? '-1' : '0')
          card.setAttribute('aria-pressed', present ? 'true' : 'false')
        }

        /**
         * Left interactive the radios would run `updateSelectedGift`, which
         * removes every sample but the one picked. CSS alone does not cover
         * this: a radio group is also driven by the keyboard.
         */
        var input = card.querySelector('input')

        if (!input) {
          return
        }

        input.disabled = true
        input.tabIndex = -1
        input.setAttribute('aria-hidden', 'true')
      })
    })
  }

  /**
   * Run cart work with `running` held, then look again once it is done.
   * - A failed request (a sample sold out, a line the compiled handler already
   *   removed) leaves the cart status at `error`, which `reconcile()` waits
   *   out forever. Re-reading the cart settles it without triggering the
   *   compiled handler.
   * @param {Function} work - Returns a promise.
   */
  function runExclusive(work) {
    running = true

    work()
      .catch(function() {})
      .then(function() {
        if (store.state.cart.status.label !== 'error') {
          return null
        }

        return store.dispatch('cart/setResponse').catch(function() {})
      })
      .then(function() {
        running = false
        paint()
        queueReconcile()
      })
  }

  /**
   * Bring the cart in line with the active tier.
   * - One kind of change per pass: removals first, then the fill once the
   *   cart has settled again. The compiled handler may add the tier's first
   *   sample in between, which the fill then counts.
   */
  function reconcileTiers() {
    syncTierList()

    var samples = getSamples()
    var items = getItems()
    var tierIds = {}

    getVariantIds(samples).forEach(function(id) {
      tierIds[id] = true
    })

    var gifts = []
    var hasProduct = false

    items.forEach(function(item) {
      if (isGift(item)) {
        gifts.push(item)
        return
      }

      hasProduct = true
    })

    /**
     * No products: every sample goes, including ones from another tier that
     * the compiled handler no longer recognises. One line per pass, since the
     * handler removes listed ones in parallel after each update.
     */
    if (!hasProduct) {
      if (!gifts.length) {
        paint()
        return
      }

      var unlisted = gifts.filter(function(item) {
        return !tierIds[item.id]
      })

      var leftover = unlisted.length ? unlisted[0] : gifts[gifts.length - 1]

      runExclusive(function() {
        return removeLines([leftover])
      })

      return
    }

    /**
     * Line items are listed newest first, so the samples kept are the newest
     * `MAX_SAMPLES` of the tier and the surplus is the oldest.
     */
    var kept = gifts.filter(function(item) {
      return tierIds[item.id]
    })

    var remove = gifts
      .filter(function(item) {
        return !tierIds[item.id]
      })
      .concat(kept.slice(MAX_SAMPLES))

    if (remove.length) {
      runExclusive(function() {
        return removeLines(remove)
      })

      return
    }

    var present = {}

    kept.forEach(function(item) {
      present[item.id] = true
    })

    var missing = []

    getVariantIds(samples).forEach(function(id) {
      if (kept.length + missing.length >= MAX_SAMPLES) {
        return
      }

      if (present[id] || abandoned[id]) {
        return
      }

      missing.push(id)
    })

    var excess = kept.filter(function(item) {
      return item.quantity > 1
    })

    if (!missing.length && !excess.length) {
      paint()
      return
    }

    runExclusive(function() {
      var work = Promise.resolve()

      if (missing.length) {
        work = work.then(function() {
          return addMissing(missing)
        })
      }

      if (excess.length) {
        work = work.then(function() {
          return collapseQuantities(excess)
        })
      }

      return work
    })
  }

  /**
   * Bring the cart in line with the sample list.
   */
  function reconcile() {
    if (running || !store) {
      return
    }

    if (!isSettled()) {
      return
    }

    if (isTierMode()) {
      reconcileTiers()
      return
    }

    var samples = getSamples()
    var items = getItems()

    var wanted = []

    samples.forEach(function(product) {
      var variant = getVariant(product)

      if (!variant || abandoned[variant.id]) {
        return
      }

      wanted.push(variant.id)
    })

    if (!wanted.length) {
      paint()
      return
    }

    var present = {}
    var hasProduct = false

    items.forEach(function(item) {
      if (isGift(item)) {
        present[item.id] = item
        return
      }

      hasProduct = true
    })

    /**
     * Removal is left to the built-in handler, which takes one sample off per
     * cart update until the cart is empty.
     */
    if (!hasProduct) {
      paint()
      return
    }

    var missing = wanted.filter(function(id) {
      return !present[id]
    })

    var excess = wanted
      .filter(function(id) {
        return present[id] && present[id].quantity > 1
      })
      .map(function(id) {
        return present[id]
      })

    if (!missing.length && !excess.length) {
      paint()
      return
    }

    running = true

    var work = Promise.resolve()

    if (missing.length) {
      work = work.then(function() {
        return addMissing(missing)
      })
    }

    if (excess.length) {
      work = work.then(function() {
        return collapseQuantities(excess)
      })
    }

    work.catch(function() {}).then(function() {
      running = false
      paint()
    })
  }

  /**
   * Swap the sample on a clicked card into the cart.
   * - Below `MAX_SAMPLES` it is added; at the limit the oldest sample makes
   *   room for it. A card already in the cart does nothing: there is no
   *   deselect, it would only fight the auto-fill and the compiled handler.
   * - `running` is held from the removal to the add, so `reconcile()` cannot
   *   refill the gap in between.
   * @param {Element} card - Clicked `.free-samples__card`.
   */
  function swap(card) {
    if (running || !isSettled()) {
      return
    }

    var root = card.closest('.free-samples')

    if (!root) {
      return
    }

    var index = Array.prototype.indexOf.call(root.querySelectorAll('.free-samples__card'), card)
    var variant = getVariant(getSamples()[index])

    if (!variant || abandoned[variant.id]) {
      return
    }

    var gifts = getItems().filter(isGift)

    var inCart = gifts.some(function(item) {
      return item.id === variant.id
    })

    if (inCart) {
      return
    }

    var remove = gifts.length >= MAX_SAMPLES ? gifts.slice(MAX_SAMPLES - 1) : []

    runExclusive(function() {
      return removeLines(remove).then(function() {
        return addSamples([variant.id]).catch(function() {
          abandoned[variant.id] = true
        })
      })
    })
  }

  /**
   * Listen for card clicks.
   * - Delegated to `document`, since Vue re-renders the drawer.
   * - The disabled radios have `pointer-events: none`, so the click lands on
   *   the card; Enter and Space cover the keyboard.
   */
  function listen() {
    function getCard(event) {
      if (!isTierMode() || !store || !event.target.closest) {
        return null
      }

      return event.target.closest('.free-samples__card')
    }

    document.addEventListener('click', function(event) {
      var card = getCard(event)

      if (card) {
        swap(card)
      }
    })

    document.addEventListener('keydown', function(event) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return
      }

      var card = getCard(event)

      if (!card || event.target !== card) {
        return
      }

      event.preventDefault()
      swap(card)
    })
  }

  /**
   * Queue a repaint.
   * - Once after Vue has flushed its render, and again shortly after to catch
   *   the cart drawer mounting its components on first open.
   */
  function queuePaint() {
    window.requestAnimationFrame(paint)
    window.clearTimeout(paintTimer)
    paintTimer = window.setTimeout(paint, 150)
  }

  /**
   * Queue a reconcile once the cart has fallen quiet.
   */
  function queueReconcile() {
    window.clearTimeout(timer)
    timer = window.setTimeout(reconcile, DEBOUNCE)
  }

  /**
   * Watch the cart and run once for the cart restored on load.
   * @param {Object} vuexStore - Vuex store.
   */
  function start(vuexStore) {
    store = vuexStore
    tiers = readTiers()

    if (store.state.cart && store.state.cart.freeSamples) {
      baseTitle = store.state.cart.freeSamples.title || ''
    }

    store.subscribe(function(mutation) {
      if (mutation.type === 'cart/SET_FREE_SAMPLES' && !committing) {
        baseTitle = (mutation.payload && mutation.payload.title) || ''
      }

      queuePaint()
      queueReconcile()
    })

    listen()
    queuePaint()
    queueReconcile()
  }

  /**
   * Wait for Vue to mount.
   * @param {Number} attempt - Current attempt.
   */
  function boot(attempt) {
    var vuexStore = findStore()

    if (vuexStore) {
      start(vuexStore)
      return
    }

    if (attempt >= BOOT_ATTEMPTS) {
      return
    }

    window.setTimeout(function() {
      boot(attempt + 1)
    }, BOOT_INTERVAL)
  }

  boot(0)
})()
